import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";
import axios from "axios";
import { Skeleton } from "antd";

import AdminRoutes from "../apps/admin/Routes";
import AdminMontessoriRoutes from "../apps/admin/AdminMontessoriRoutes";
import InstructorRoutes from "../apps/instructor/Routes";
import InstructorMontessoriRoutes from "../apps/instructor/InstructorMontessoriRoutes";
import StudentRoutes from "../apps/student/Routes";
import StudentMontessoriRoutes from "../apps/student/StudentMontessoriRoutes";

import ParentRoutes from "../apps/parent/Routes";
import ParentMontessoriRoutes from "../apps/parent/ParentMontessoriRoutes";
import SuperAdminRoutes from "../apps/superAdmin/Route";
import GroupConsoleRoutes from "../apps/groupConsole/Routes";
import useIsGroupConsole from "../apps/groupConsole/useIsGroupConsole";
import DashboardLayout from "../layout/DashboardLayout";
import { selectUser, selectSchoolId } from "../redux/slices/auth.slice";
import { serverUrl } from "../nestserver";

const DashboardRoutes = () => {
  const navigate = useNavigate();

  const user = useSelector(selectUser);
  const role = useMemo(() => user?.role, [user]);
  const schoolId = useSelector(selectSchoolId);

  // A group admin is in group mode by default, and drops out of it only once
  // they are actually on an /dashboard/admin/... path — i.e. after picking a
  // branch. Role stays "admin" throughout, so the branch switcher and every
  // admin path keep working exactly as before; this hook is the one place
  // that rule lives (LeftSidebar.js uses the same one for its rail).
  const isGroupConsole = useIsGroupConsole();

  async function fetchSchool(id) {
    const res = await axios({
      method: "get",
      url: `${serverUrl}/school/${id}`,
      headers: {
        Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
      },
    });
    return res.data;
  }

  const school = useQuery(["school", schoolId], () => fetchSchool(schoolId), {
    enabled: !!schoolId && role !== "super_admin" && !isGroupConsole,
  });

  const isMontessoriEnabled =
    school?.data?.result?.data?.is_montessori_enabled === true;

  useEffect(() => {
    if (user === null) {
      navigate("/auth/login", { replace: true });
    }
  }, [user, navigate]);

  const isLoadingSchool =
    school?.isLoading && role !== "super_admin" && !isGroupConsole;

  return (
    <DashboardLayout>
      {isGroupConsole ? (
        <GroupConsoleRoutes />
      ) : isLoadingSchool ? (
        <div className="p-8">
          <Skeleton active paragraph={{ rows: 10 }} />
        </div>
      ) : (
        (() => {
          switch (role) {
            case "student":
              return isMontessoriEnabled ? (
                <StudentMontessoriRoutes />
              ) : (
                <StudentRoutes />
              );
            case "instructor":
              return isMontessoriEnabled ? (
                <InstructorMontessoriRoutes />
              ) : (
                <InstructorRoutes />
              );
            case "admin":
              return isMontessoriEnabled ? (
                <AdminMontessoriRoutes />
              ) : (
                <AdminRoutes />
              );
            case "parent":
              return isMontessoriEnabled ? (
                <ParentMontessoriRoutes />
              ) : (
                <ParentRoutes />
              );
            case "super_admin":
              return <SuperAdminRoutes />;
            default:
              return null;
          }
        })()
      )}
    </DashboardLayout>
  );
};

export default DashboardRoutes;
